import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { FaBookmark, FaCalendarAlt, FaCheck, FaLeaf, FaPaperPlane, FaSave, FaTags, FaTimes, FaTractor, FaUserCheck } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";

import myConfig from "../../configs/config";
import i18n from "../../i18n";
import { fetchFarmOperators } from "../Radio/radioApi";
import {
  SERVICE_ORDER_TYPES,
  showsAudioFields,
  showsHarvestLossFields,
  showsMachineFields,
  showsFertilizationFields,
  showsPlantingFields,
  showsSprayRecipeFields,
  showsTrackHeadingFields,
  normalizeServiceOrderType,
} from "../../constants/serviceOrderTypes";
import {
  defaultScheduleWindow,
  toDatetimeLocalValue,
  toLocalISOString,
} from "./observationDateUtils";
import ServiceOrderAudioRecorder from "./ServiceOrderAudioRecorder";
import ServiceOrderLocationFields from "./ServiceOrderLocationFields";
import ServiceOrderMachineSelector from "./ServiceOrderMachineSelector";
import ServiceOrderProductsPanel from "./ServiceOrderProductsPanel";
import ServiceOrderHarvestLossPanel from "./ServiceOrderHarvestLossPanel";
import ServiceOrderPlantingPanel from "./ServiceOrderPlantingPanel";
import ServiceOrderFertilizationPanel from "./ServiceOrderFertilizationPanel";
import ServiceOrderTrackHeadingFields from "./ServiceOrderTrackHeadingFields";
import { DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT } from "../FieldApp/harvestLossMetrics";
import {
  applicationAreaFromPlan,
  buildHarvestLossMonitoringPayload,
  buildServiceOrderPlan,
  buildTemplateServiceOrderPlan,
  createEmptyProductRow,
  DEFAULT_CALIBRATION_TOLERANCE_PERCENT,
  DEFAULT_HARVEST_LOSS_KG_PER_BAG,
  DEFAULT_HARVEST_LOSS_MIN_REPETITIONS,
  formatProductsUsedText,
  harvestLossMonitoringFromPlan,
  machineIdentsFromPlan,
  parsePositiveNumber,
  productRowsFromPlan,
  templatePlanHasRecipeData,
} from "./serviceOrderProducts";
import {
  buildPlantingPayload,
  DEFAULT_OPERATOR_SUPPLY_UNIT,
  DEFAULT_SEEDS_PER_PACK,
  plantingFromPlan,
} from "./serviceOrderPlanting";
import {
  buildFertilizationPayload,
  fertilizationFromPlan,
} from "./serviceOrderFertilization";
import {
  publishServiceOrder,
  uploadObservationAudio,
} from "../../utils/serviceOrderSyncApi";
import { releaseModalBlockers } from "../../utils/nestedMuiModalProps";
import "./CreateScheduleModal.css";
import "./ServiceOrderProductsPanel.css";
import "./ServiceOrderHarvestLossPanel.css";
import "./ServiceOrderPlantingPanel.css";
import "./ServiceOrderAudioRecorder.css";

function CreateScheduleModal({
  show,
  onHide,
  farmId,
  onCreated,
  defaultServiceType = "",
  requireMachine = false,
  editingOrder = null,
}) {
  const t = useCallback(
    (key, options) => i18n.t(key, { ...options, lng: "pt_BR" }),
    []
  );
  const defaults = defaultScheduleWindow();
  const [operators, setOperators] = useState([]);
  const [machines, setMachines] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [savedTemplateLabel, setSavedTemplateLabel] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const operatorsSectionRef = useRef(null);
  const machinesSectionRef = useRef(null);
  const templateNameInputRef = useRef(null);
  const targetRateSyncedRef = useRef(true);
  const previousServiceTypeRef = useRef("");
  const isEditing = Boolean(editingOrder && editingOrder.id);

  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [serviceType, setServiceType] = useState(defaultServiceType || "");
  const [selectedMachineIdents, setSelectedMachineIdents] = useState([]);
  const [productRows, setProductRows] = useState([createEmptyProductRow()]);
  const [sprayVolumePerHa, setSprayVolumePerHa] = useState("");
  const [targetRateLHa, setTargetRateLHa] = useState("");
  const [calibrationTolerancePercent, setCalibrationTolerancePercent] = useState(
    String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT)
  );
  const [tankCapacityL, setTankCapacityL] = useState("");
  const [applicationArea, setApplicationArea] = useState(null);
  const [description, setDescription] = useState("");
  const [audioBlob, setAudioBlob] = useState(null);
  const [selectedServiceFarmIds, setSelectedServiceFarmIds] = useState([]);
  const [selectedLandPlotIds, setSelectedLandPlotIds] = useState([]);
  const [selectedOperatorIds, setSelectedOperatorIds] = useState([]);
  const [trackHeadingPresetId, setTrackHeadingPresetId] = useState("");
  const [trackHeadingAudioBlob, setTrackHeadingAudioBlob] = useState(null);
  const [trackHeadingOnFarmMap, setTrackHeadingOnFarmMap] = useState(true);
  const [sendMode, setSendMode] = useState("now");
  const [visibleAt, setVisibleAt] = useState(defaults.visible);
  const [harvestLossPmsG, setHarvestLossPmsG] = useState("");
  const [harvestLossToleratedKgHa, setHarvestLossToleratedKgHa] = useState("");
  const [harvestLossAlertThresholdPercent, setHarvestLossAlertThresholdPercent] =
    useState(String(DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT));
  const [harvestLossKgPerBag, setHarvestLossKgPerBag] = useState(
    String(DEFAULT_HARVEST_LOSS_KG_PER_BAG)
  );
  const [harvestLossMinRepetitions, setHarvestLossMinRepetitions] = useState(
    String(DEFAULT_HARVEST_LOSS_MIN_REPETITIONS)
  );
  const [harvestLossPreviewSeedCount, setHarvestLossPreviewSeedCount] = useState("");
  const [plantingCultivar, setPlantingCultivar] = useState("");
  const [plantingSeedsThousandPerHa, setPlantingSeedsThousandPerHa] = useState("");
  const [plantingRowSpacingCm, setPlantingRowSpacingCm] = useState("");
  const [plantingPmsG, setPlantingPmsG] = useState("");
  const [plantingSeedsPerPack, setPlantingSeedsPerPack] = useState(
    String(DEFAULT_SEEDS_PER_PACK)
  );
  const [plantingOperatorSupplyUnit, setPlantingOperatorSupplyUnit] = useState(
    DEFAULT_OPERATOR_SUPPLY_UNIT
  );
  const [plantingFertilizerKgHa, setPlantingFertilizerKgHa] = useState("");
  const [plantingInoculantWaterLHa, setPlantingInoculantWaterLHa] = useState("");
  const [plantingBinderRecommendation, setPlantingBinderRecommendation] =
    useState("");
  const [fertilizerName, setFertilizerName] = useState("");
  const [fertilizerKgHa, setFertilizerKgHa] = useState("");

  const resetPlantingFields = () => {
    setPlantingCultivar("");
    setPlantingSeedsThousandPerHa("");
    setPlantingRowSpacingCm("");
    setPlantingPmsG("");
    setPlantingSeedsPerPack(String(DEFAULT_SEEDS_PER_PACK));
    setPlantingOperatorSupplyUnit(DEFAULT_OPERATOR_SUPPLY_UNIT);
    setPlantingFertilizerKgHa("");
    setPlantingInoculantWaterLHa("");
    setPlantingBinderRecommendation("");
  };

  const applyPlantingFields = (planting) => {
    if (!planting) {
      resetPlantingFields();
      return;
    }
    setPlantingCultivar(planting.cultivar || "");
    setPlantingSeedsThousandPerHa(
      planting.seedsThousandPerHa > 0 ? String(planting.seedsThousandPerHa) : ""
    );
    setPlantingRowSpacingCm(
      planting.rowSpacingCm > 0 ? String(planting.rowSpacingCm) : ""
    );
    setPlantingPmsG(planting.pmsG > 0 ? String(planting.pmsG) : "");
    setPlantingSeedsPerPack(
      String(planting.seedsPerPack || DEFAULT_SEEDS_PER_PACK)
    );
    setPlantingOperatorSupplyUnit(
      planting.operatorSupplyUnit || DEFAULT_OPERATOR_SUPPLY_UNIT
    );
    setPlantingFertilizerKgHa(
      planting.fertilizerKgHa > 0 ? String(planting.fertilizerKgHa) : ""
    );
    setPlantingInoculantWaterLHa(
      planting.inoculantWaterLHa > 0 ? String(planting.inoculantWaterLHa) : ""
    );
    setPlantingBinderRecommendation(planting.binderRecommendation || "");
  };

  const resetFertilizationFields = () => {
    setFertilizerName("");
    setFertilizerKgHa("");
  };

  const applyFertilizationFields = (fertilization) => {
    if (!fertilization) {
      resetFertilizationFields();
      return;
    }
    setFertilizerName(fertilization.fertilizerName || "");
    setFertilizerKgHa(
      fertilization.fertilizerKgHa > 0 ? String(fertilization.fertilizerKgHa) : ""
    );
  };

  const authHeaders = {
    Authorization: `Bearer ${localStorage.getItem("client-token")}`,
  };

  const handleHide = useCallback(() => {
    onHide();
    releaseModalBlockers();
  }, [onHide]);

  useEffect(() => {
    if (!show) {
      releaseModalBlockers();
      return undefined;
    }

    releaseModalBlockers();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        handleHide();
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      releaseModalBlockers();
    };
  }, [show, handleHide]);

  const loadTemplates = async () => {
    try {
      const response = await axios.get(
        `${myConfig.API_URL}/service-order-template/`,
        {
          params: { farm_id: farmId },
          headers: authHeaders,
        }
      );
      const data = Array.isArray(response.data)
        ? response.data
        : response.data.results || [];
      setTemplates(data);
    } catch (error) {
      setTemplates([]);
    }
  };

  useEffect(() => {
    if (!show || !farmId) return;

    const loadOptions = async () => {
      setLoadingOptions(true);
      try {
        const operatorList = await fetchFarmOperators(farmId);
        setOperators(operatorList);
        if (!operatorList.length) {
          toast.warn(
            t("Nenhum operador encontrado nesta fazenda."),
            { position: "bottom-right" }
          );
        }

        try {
          const machinesResponse = await axios.get(`${myConfig.API_URL}/equipment/`, {
            params: { farm_id: farmId },
            headers: authHeaders,
          });
          const machineList = Array.isArray(machinesResponse.data)
            ? machinesResponse.data
            : machinesResponse.data.results || [];
          setMachines(
            machineList.filter(
              (item) => item && item.ident && item.classification !== "implemento"
            )
          );
        } catch (machineError) {
          setMachines([]);
        }
      } catch (error) {
        toast.error(
          t("Falha ao carregar operadores. Tente novamente."),
          { position: "bottom-right" }
        );
        setOperators([]);
      }

      await loadTemplates();
      setLoadingOptions(false);
    };

    loadOptions();
  }, [show, farmId, t]);

  useEffect(() => {
    if (!show) {
      const nextDefaults = defaultScheduleWindow();
      setStartDate(nextDefaults.start);
      setEndDate(nextDefaults.end);
      setServiceType(defaultServiceType || "");
      setSelectedMachineIdents([]);
      setProductRows([createEmptyProductRow()]);
      setSprayVolumePerHa("");
      setTargetRateLHa("");
      setCalibrationTolerancePercent(String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT));
      setTankCapacityL("");
      setApplicationArea(null);
      setDescription("");
      setAudioBlob(null);
      setSelectedServiceFarmIds([]);
      setSelectedLandPlotIds([]);
      setSelectedOperatorIds([]);
      setTrackHeadingPresetId("");
      setTrackHeadingAudioBlob(null);
      setTrackHeadingOnFarmMap(true);
      setSendMode("now");
      setVisibleAt(nextDefaults.visible);
      setSelectedTemplateId("");
      setTemplateName("");
      setShowSaveTemplate(false);
      setSavedTemplateLabel("");
      setFormErrors({});
      targetRateSyncedRef.current = true;
      setHarvestLossPmsG("");
      setHarvestLossToleratedKgHa("");
      setHarvestLossAlertThresholdPercent(
        String(DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT)
      );
      setHarvestLossKgPerBag(String(DEFAULT_HARVEST_LOSS_KG_PER_BAG));
      setHarvestLossMinRepetitions(String(DEFAULT_HARVEST_LOSS_MIN_REPETITIONS));
      setHarvestLossPreviewSeedCount("");
      resetPlantingFields();
      resetFertilizationFields();
    }
  }, [show]);

  useEffect(() => {
    if (!show || !editingOrder) return;

    const plan = editingOrder.service_order_plan || {};
    setStartDate(toDatetimeLocalValue(editingOrder.start_date));
    setEndDate(toDatetimeLocalValue(editingOrder.end_date));
    setServiceType(
      normalizeServiceOrderType(editingOrder.service_type || defaultServiceType || "")
    );
    setSelectedMachineIdents(machineIdentsFromPlan(plan));
    setProductRows(productRowsFromPlan(plan));
    setSprayVolumePerHa(
      plan.spray_volume_per_ha > 0 ? String(plan.spray_volume_per_ha) : ""
    );
    setTargetRateLHa(
      plan.target_rate_l_ha > 0 ? String(plan.target_rate_l_ha) : ""
    );
    setTankCapacityL(
      plan.tank_capacity_l > 0 ? String(plan.tank_capacity_l) : ""
    );
    setApplicationArea(applicationAreaFromPlan(plan));
    setDescription(editingOrder.description || "");
    setSelectedServiceFarmIds(editingOrder.service_farm_ids || []);
    setSelectedLandPlotIds(editingOrder.service_land_plot_ids || []);
    setSelectedOperatorIds(
      (editingOrder.assigned_operators || []).map((operator) => operator.id)
    );
    setCalibrationTolerancePercent(
      plan.calibration_tolerance_percent != null
        ? String(plan.calibration_tolerance_percent)
        : String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT)
    );
    const harvestLoss = harvestLossMonitoringFromPlan(plan);
    if (harvestLoss) {
      setHarvestLossPmsG(String(harvestLoss.pmsG));
      setHarvestLossToleratedKgHa(
        harvestLoss.toleratedLossKgHa > 0
          ? String(harvestLoss.toleratedLossKgHa)
          : ""
      );
      setHarvestLossKgPerBag(String(harvestLoss.kgPerBag));
      setHarvestLossMinRepetitions(String(harvestLoss.minRepetitions));
      setHarvestLossAlertThresholdPercent(
        String(harvestLoss.alertThresholdPercent)
      );
    } else {
      setHarvestLossPmsG("");
      setHarvestLossToleratedKgHa("");
      setHarvestLossAlertThresholdPercent(
        String(DEFAULT_HARVEST_LOSS_ALERT_THRESHOLD_PERCENT)
      );
      setHarvestLossKgPerBag(String(DEFAULT_HARVEST_LOSS_KG_PER_BAG));
      setHarvestLossMinRepetitions(String(DEFAULT_HARVEST_LOSS_MIN_REPETITIONS));
    }
    applyPlantingFields(plantingFromPlan(plan));
    applyFertilizationFields(fertilizationFromPlan(plan));
    if (editingOrder.visible_at) {
      setSendMode("scheduled");
      setVisibleAt(toDatetimeLocalValue(editingOrder.visible_at));
    } else {
      setSendMode("now");
    }
    targetRateSyncedRef.current = false;
  }, [show, editingOrder, defaultServiceType]);

  useEffect(() => {
    if (!showsSprayRecipeFields(serviceType)) return;
    const spray = parsePositiveNumber(sprayVolumePerHa);
    if (!spray) return;
    if (targetRateSyncedRef.current) {
      setTargetRateLHa(String(spray));
    }
  }, [sprayVolumePerHa, serviceType]);

  useEffect(() => {
    if (!show) {
      previousServiceTypeRef.current = "";
      return;
    }

    const previousType = previousServiceTypeRef.current;
    previousServiceTypeRef.current = serviceType;

    if (!previousType || previousType === serviceType) return;
    if (showsSprayRecipeFields(previousType) && !showsSprayRecipeFields(serviceType)) {
      setSelectedTemplateId("");
      setProductRows([createEmptyProductRow()]);
      setSprayVolumePerHa("");
      setTargetRateLHa("");
      setCalibrationTolerancePercent(String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT));
      setTankCapacityL("");
      setTemplateName("");
      setShowSaveTemplate(false);
      setSavedTemplateLabel("");
      targetRateSyncedRef.current = true;
    }
    if (!showsMachineFields(serviceType)) {
      setSelectedMachineIdents([]);
    }
    if (!showsAudioFields(serviceType)) {
      setAudioBlob(null);
    }
    if (!showsTrackHeadingFields(serviceType)) {
      setTrackHeadingPresetId("");
      setTrackHeadingAudioBlob(null);
      setTrackHeadingOnFarmMap(true);
    }
  }, [serviceType, show]);

  const toggleOperator = (operatorId) => {
    setFormErrors((current) => {
      if (!current.operators) return current;
      const next = { ...current };
      delete next.operators;
      return next;
    });
    setSelectedOperatorIds((current) =>
      current.includes(operatorId)
        ? current.filter((id) => id !== operatorId)
        : [...current, operatorId]
    );
  };

  const scrollToFirstError = (errors) => {
    if (errors.machines && machinesSectionRef.current) {
      machinesSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    if (errors.operators && operatorsSectionRef.current) {
      operatorsSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!serviceType) {
      errors.serviceType = t("Selecione o tipo de serviço.");
    }
    if (!description.trim()) {
      errors.description = t("Informe a descrição da ordem de serviço.");
    }
    if (!startDate || !endDate) {
      errors.dates = t("Informe início e fim do serviço.");
    }
    if (selectedOperatorIds.length === 0) {
      errors.operators = t("Selecione ao menos um operador para enviar.");
    }
    if (
      showsMachineFields(serviceType) &&
      requireMachine &&
      selectedMachineIdents.length === 0
    ) {
      errors.machines = t("Selecione ao menos uma máquina para esta ordem de calibragem.");
    }
    if (sendMode === "scheduled" && !visibleAt) {
      errors.visibleAt = t("Informe quando a ordem deve aparecer no app.");
    }
    if (showsHarvestLossFields(serviceType) && !parsePositiveNumber(harvestLossPmsG)) {
      errors.harvestLossPms = t("Informe o PMS (peso de mil sementes) em gramas.");
    }
    if (showsPlantingFields(serviceType)) {
      if (!String(plantingCultivar || "").trim()) {
        errors.plantingCultivar = t("Informe o nome da cultivar.");
      }
      if (!parsePositiveNumber(plantingSeedsThousandPerHa)) {
        errors.plantingSeeds = t(
          "Informe a recomendação em mil sementes por hectare."
        );
      }
      if (!parsePositiveNumber(plantingRowSpacingCm)) {
        errors.plantingSpacing = t(
          "Informe o espaçamento entre linhas da plantadeira."
        );
      }
    }
    if (showsFertilizationFields(serviceType)) {
      if (!String(fertilizerName || "").trim()) {
        errors.fertilizerName = t("Informe o nome do fertilizante.");
      }
      if (!parsePositiveNumber(fertilizerKgHa)) {
        errors.fertilizerKgHa = t(
          "Informe a taxa-alvo em quilos por hectare."
        );
      }
    }

    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      return true;
    }

    const firstMessage = Object.values(errors)[0];
    toast.error(firstMessage, { position: "top-center" });
    scrollToFirstError(errors);
    return false;
  };

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;

    const template = templates.find(
      (item) => String(item.id) === String(templateId)
    );
    if (!template) return;

    setDescription(template.description || "");
    setServiceType(normalizeServiceOrderType(template.service_type || ""));

    const plan = template.service_order_plan || null;
    if (plan && templatePlanHasRecipeData(plan)) {
      setProductRows(productRowsFromPlan(plan));
      setSprayVolumePerHa(
        plan.spray_volume_per_ha > 0 ? String(plan.spray_volume_per_ha) : ""
      );
      setTargetRateLHa(
        plan.target_rate_l_ha > 0 ? String(plan.target_rate_l_ha) : ""
      );
      setTankCapacityL(
        plan.tank_capacity_l > 0 ? String(plan.tank_capacity_l) : ""
      );
      setCalibrationTolerancePercent(
        plan.calibration_tolerance_percent != null
          ? String(plan.calibration_tolerance_percent)
          : String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT)
      );
      setSelectedMachineIdents(machineIdentsFromPlan(plan));
      targetRateSyncedRef.current = false;
      return;
    }

    applyPlantingFields(plantingFromPlan(plan));
    applyFertilizationFields(fertilizationFromPlan(plan));

    if (template.products_used) {
      setDescription(
        [template.description, template.products_used]
          .filter(Boolean)
          .join("\n\n")
          .trim()
      );
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error(t("Informe um nome para salvar a receita."), {
        position: "top-center",
      });
      if (templateNameInputRef.current) {
        templateNameInputRef.current.focus();
      }
      return;
    }

    const templatePlan = buildTemplateServiceOrderPlan(serviceOrderPlan);
    if (!templatePlanHasRecipeData(templatePlan)) {
      toast.error(
        t(
          "Informe ao menos um produto, a vazão de calda ou a capacidade do tanque antes de salvar a receita."
        ),
        { position: "top-center" }
      );
      return;
    }

    const finishTemplateSave = async (response, savedStructuredPlan) => {
      const savedName = templateName.trim();
      const savedId = response.data && response.data.id;
      toast.success(
        savedStructuredPlan
          ? t("Receita salva para uso nas próximas ordens.")
          : t(
              "Receita salva com produtos e dosagens no texto. Atualize a API para restaurar os campos automaticamente."
            ),
        { position: "top-center", autoClose: savedStructuredPlan ? 5000 : 10000 }
      );
      setShowSaveTemplate(false);
      setTemplateName("");
      setSavedTemplateLabel(savedName);
      if (savedId) {
        setSelectedTemplateId(String(savedId));
      }
      await loadTemplates();
    };

    const basePayload = {
      farm: parseInt(farmId, 10),
      name: templateName.trim(),
      service_type: serviceType,
      description: description.trim() || templateName.trim(),
      products_used: formatProductsUsedText(templatePlan, t),
    };

    setSavingTemplate(true);
    try {
      try {
        const response = await axios.post(
          `${myConfig.API_URL}/service-order-template/`,
          {
            ...basePayload,
            service_order_plan: templatePlan,
          },
          { headers: authHeaders }
        );
        await finishTemplateSave(response, true);
      } catch (firstError) {
        const status = firstError && firstError.response && firstError.response.status;
        if (status !== 400 && status !== 500) {
          throw firstError;
        }

        const response = await axios.post(
          `${myConfig.API_URL}/service-order-template/`,
          basePayload,
          { headers: authHeaders }
        );
        await finishTemplateSave(response, false);
      }
    } catch (error) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        (error.response.data.detail ||
          Object.values(error.response.data).flat().join(" "));
      toast.error(
        message || t("Não foi possível salvar a receita."),
        { position: "top-center", autoClose: 10000 }
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const applyTrackHeadingLocation = ({ serviceFarmIds, landPlotIds }) => {
    if (Array.isArray(serviceFarmIds) && serviceFarmIds.length > 0) {
      setSelectedServiceFarmIds(serviceFarmIds);
    }
    if (Array.isArray(landPlotIds) && landPlotIds.length > 0) {
      setSelectedLandPlotIds(landPlotIds);
    }
  };

  const handleApplicationAreaChange = useCallback((summary) => {
    setApplicationArea(summary || null);
  }, []);

  useEffect(() => {
    if (show && defaultServiceType) {
      setServiceType(defaultServiceType);
    }
  }, [show, defaultServiceType]);

  const harvestLossMonitoring = showsHarvestLossFields(serviceType)
    ? buildHarvestLossMonitoringPayload({
        pmsG: harvestLossPmsG,
        toleratedLossKgHa: harvestLossToleratedKgHa,
        kgPerBag: harvestLossKgPerBag,
        minRepetitions: harvestLossMinRepetitions,
        alertThresholdPercent: harvestLossAlertThresholdPercent,
      })
    : null;

  const planting = showsPlantingFields(serviceType)
    ? buildPlantingPayload({
        cultivar: plantingCultivar,
        seedsThousandPerHa: plantingSeedsThousandPerHa,
        rowSpacingCm: plantingRowSpacingCm,
        pmsG: plantingPmsG,
        seedsPerPack: plantingSeedsPerPack,
        operatorSupplyUnit: plantingOperatorSupplyUnit,
        fertilizerKgHa: plantingFertilizerKgHa,
        inoculantWaterLHa: plantingInoculantWaterLHa,
        binderRecommendation: plantingBinderRecommendation,
      })
    : null;

  const fertilization = showsFertilizationFields(serviceType)
    ? buildFertilizationPayload({
        fertilizerName,
        fertilizerKgHa,
      })
    : null;

  const serviceOrderPlan = buildServiceOrderPlan(
    showsSprayRecipeFields(serviceType)
      ? {
          productRows,
          applicationArea,
          sprayVolumePerHa,
          tankCapacityL,
          machineIdents: showsMachineFields(serviceType)
            ? selectedMachineIdents
            : [],
          targetRateLHa,
          calibrationTolerancePercent,
        }
      : {
          productRows: [],
          applicationArea,
          sprayVolumePerHa: "",
          tankCapacityL: "",
          machineIdents: showsMachineFields(serviceType)
            ? selectedMachineIdents
            : [],
          targetRateLHa: "",
          calibrationTolerancePercent: String(DEFAULT_CALIBRATION_TOLERANCE_PERCENT),
          harvestLossMonitoring,
          planting,
          fertilization,
        }
  );

  const recipeTemplates = templates.filter(
    (template) =>
      !template.service_type || template.service_type === "pulverizacao"
  );

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (
      showsSprayRecipeFields(serviceType) &&
      !parsePositiveNumber(sprayVolumePerHa)
    ) {
      toast.warn(
        t(
          "Informe a vazão de calda (L/ha) para que a taxa-alvo do check de calibragem seja enviada ao app."
        ),
        { position: "top-center", autoClose: 8000 }
      );
    }

    const visibleDate =
      sendMode === "now"
        ? toLocalISOString(toDatetimeLocalValue(new Date()))
        : toLocalISOString(visibleAt);

    setSubmitting(true);
    try {
      const productsText = formatProductsUsedText(serviceOrderPlan, t);
      const payload = {
        farm_id: parseInt(farmId, 10),
        service_type: serviceType,
        start_date: toLocalISOString(startDate),
        end_date: toLocalISOString(endDate),
        products_used: productsText,
        service_order_plan: serviceOrderPlan,
        description: description.trim(),
        visible_at: toLocalISOString(visibleDate),
        operator_ids: selectedOperatorIds,
        ...(selectedServiceFarmIds.length > 0
          ? { service_farm_ids: selectedServiceFarmIds }
          : {}),
        ...(selectedLandPlotIds.length > 0
          ? { service_land_plot_ids: selectedLandPlotIds }
          : {}),
        ...(trackHeadingPresetId
          ? {
              track_heading_preset_id: parseInt(trackHeadingPresetId, 10),
              track_heading_on_farm_map: trackHeadingOnFarmMap,
            }
          : {}),
      };

      let observationId = editingOrder && editingOrder.id;

      if (isEditing) {
        await axios.patch(
          `${myConfig.API_URL}/observation/${observationId}/`,
          payload,
          { headers: authHeaders }
        );
      } else {
        const response = await axios.post(
          `${myConfig.API_URL}/observation/`,
          payload,
          { headers: authHeaders }
        );
        observationId = response.data && response.data.id;
      }

      if (observationId && (audioBlob || trackHeadingAudioBlob)) {
        try {
          const saved = await uploadObservationAudio(observationId, {
            audioBlob,
            trackHeadingAudioBlob,
          });
          if (
            audioBlob &&
            !(saved && saved.audio_message_url)
          ) {
            toast.warn(
              t(
                "Ordem salva, mas o áudio não foi anexado. Edite a ordem, grave o áudio de novo e salve."
              ),
              { position: "top-center", autoClose: 12000 }
            );
          }
        } catch (audioError) {
          toast.error(
            t(
              "Ordem salva, mas falhou ao enviar o áudio. Edite a ordem, grave novamente e salve."
            ),
            { position: "top-center", autoClose: 12000 }
          );
        }
        if (isEditing) {
          try {
            await publishServiceOrder(observationId);
          } catch (publishError) {
            toast.warn(
              t(
                "Áudio salvo, mas não foi possível republicar no app. Use \"Sincronizar com app de campo\"."
              ),
              { position: "top-center", autoClose: 10000 }
            );
          }
        }
      }

      if (!isEditing && observationId) {
        try {
          await publishServiceOrder(observationId);
        } catch (publishError) {
          toast.warn(
            t(
              "Ordem criada, mas não foi possível publicar no app. Use \"Sincronizar com app de campo\" na aba Calibragem."
            ),
            { position: "top-center", autoClose: 10000 }
          );
        }
      }

      toast.success(
        isEditing
          ? t(
              "Alterações salvas como rascunho. Clique em \"Sincronizar com app de campo\" para enviar a nova receita. A versão anterior permanece no histórico."
            )
          : t("Ordem de serviço criada e enviada aos operadores selecionados."),
        { position: "top-center", autoClose: isEditing ? 10000 : 5000 }
      );
      setFormErrors({});
      if (onCreated) onCreated();
      handleHide();
    } catch (error) {
      const message =
        error &&
        error.response &&
        error.response.data &&
        (error.response.data.detail ||
          Object.values(error.response.data).flat().join(" "));
      toast.error(
        message || t("Erro ao salvar ordem de serviço. Tente novamente."),
        { position: "top-center" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!show) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className="create-schedule-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleHide();
        }
      }}
      role="presentation"
    >
      <div
        className="create-schedule-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-schedule-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="create-schedule-modal__header">
          <h2 id="create-schedule-title">
            <FaLeaf style={{ marginRight: 8 }} />
            {isEditing ? t("Editar ordem de serviço") : t("Nova Ordem de Serviço")}
          </h2>
          <button
            type="button"
            className="create-schedule-close"
            onClick={handleHide}
            aria-label={t("Fechar")}
          >
            <FaTimes />
          </button>
        </div>

        <form className="create-schedule-form" onSubmit={handleSubmit}>
          {Object.keys(formErrors).length > 0 ? (
            <div className="create-schedule-form-errors" role="alert">
              <strong>{t("Corrija os campos abaixo:")}</strong>
              <ul>
                {Object.values(formErrors).map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </div>
          ) : null}

          <p className="create-schedule-intro">
            {showsSprayRecipeFields(serviceType)
              ? t(
                  "Descreva o serviço, informe os produtos e envie direto para o app do operador. Não é necessário vincular máquina ou operação."
                )
              : t(
                  "Descreva o serviço e envie direto para o app do operador. Campos específicos (produtos, calda, receita) aparecem conforme o tipo de serviço."
                )}
          </p>

          {loadingOptions ? (
            <p>{t("Carregando operadores e receitas...")}</p>
          ) : (
            <>
              <div className="create-schedule-field">
                <label htmlFor="schedule-service-type">
                  <FaTags style={{ marginRight: 6 }} />
                  {t("Tipo de serviço")}
                </label>
                <select
                  id="schedule-service-type"
                  className="form-control"
                  value={serviceType}
                  onChange={(event) => setServiceType(event.target.value)}
                  required
                >
                  <option value="">{t("Selecione o tipo")}</option>
                  {SERVICE_ORDER_TYPES.filter(
                    (item) => item.value !== "ponto_interesse"
                  ).map((item) => (
                    <option key={item.value} value={item.value}>
                      {t(item.label)}
                    </option>
                  ))}
                </select>
              </div>

              {showsSprayRecipeFields(serviceType) ? (
                <div className="create-schedule-field">
                  <label htmlFor="schedule-template">
                    <FaBookmark style={{ marginRight: 6 }} />
                    {t("Usar receita salva")}
                  </label>
                  <select
                    id="schedule-template"
                    className="form-control"
                    value={selectedTemplateId}
                    onChange={(event) => applyTemplate(event.target.value)}
                  >
                    <option value="">{t("Começar em branco")}</option>
                    {recipeTemplates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {showsMachineFields(serviceType) ? (
              <div
                className={`create-schedule-field${
                  formErrors.machines ? " create-schedule-field--error" : ""
                }`}
                ref={machinesSectionRef}
              >
                <label>
                  <FaTractor style={{ marginRight: 6 }} />
                  {t("Máquinas")}
                  {requireMachine ? " *" : ""}
                </label>
                <p className="create-schedule-hint">
                  {showsSprayRecipeFields(serviceType)
                    ? t(
                        "No app de campo o operador não escolhe a máquina — marque aqui para quais equipamentos esta ordem e a taxa-alvo de calibragem se aplicam."
                      )
                    : t(
                        "No app de campo o operador não escolhe a máquina — marque aqui para quais equipamentos esta ordem se aplica."
                      )}
                </p>
                {formErrors.machines ? (
                  <p className="create-schedule-field-error">{formErrors.machines}</p>
                ) : null}
                <ServiceOrderMachineSelector
                  farmId={farmId}
                  machines={machines}
                  selectedMachineIdents={selectedMachineIdents}
                  onSelectedMachineIdentsChange={(next) => {
                    setFormErrors((current) => {
                      if (!current.machines) return current;
                      const updated = { ...current };
                      delete updated.machines;
                      return updated;
                    });
                    setSelectedMachineIdents(next);
                  }}
                  requireMachine={requireMachine}
                  error={formErrors.machines}
                  t={t}
                />
              </div>
              ) : null}

              <div className="create-schedule-row">
                <div className="create-schedule-field">
                  <label htmlFor="schedule-start">
                    <FaCalendarAlt style={{ marginRight: 6 }} />
                    {t("Início do serviço")}
                  </label>
                  <input
                    id="schedule-start"
                    type="datetime-local"
                    className="form-control"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    required
                  />
                </div>
                <div className="create-schedule-field">
                  <label htmlFor="schedule-end">{t("Fim do serviço")}</label>
                  <input
                    id="schedule-end"
                    type="datetime-local"
                    className="form-control"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    required
                  />
                </div>
              </div>

              <ServiceOrderLocationFields
                farmId={farmId}
                selectedServiceFarmIds={selectedServiceFarmIds}
                selectedLandPlotIds={selectedLandPlotIds}
                onServiceFarmIdsChange={setSelectedServiceFarmIds}
                onLandPlotIdsChange={setSelectedLandPlotIds}
                onApplicationAreaChange={handleApplicationAreaChange}
                initialApplicationArea={applicationArea}
                t={t}
              />

              {showsSprayRecipeFields(serviceType) ? (
                <div className="create-schedule-field">
                  <ServiceOrderProductsPanel
                    applicationArea={applicationArea}
                    productRows={productRows}
                    onProductRowsChange={setProductRows}
                    sprayVolumePerHa={sprayVolumePerHa}
                    onSprayVolumePerHaChange={setSprayVolumePerHa}
                    targetRateLHa={targetRateLHa}
                    onTargetRateLHaChange={(value) => {
                      targetRateSyncedRef.current = false;
                      setTargetRateLHa(value);
                    }}
                    calibrationTolerancePercent={calibrationTolerancePercent}
                    onCalibrationTolerancePercentChange={setCalibrationTolerancePercent}
                    tankCapacityL={tankCapacityL}
                    onTankCapacityLChange={setTankCapacityL}
                    serviceType={serviceType}
                    t={t}
                  />
                </div>
              ) : null}

              {showsSprayRecipeFields(serviceType) ? (
              <div className="create-schedule-template-panel">
                <div className="create-schedule-template-panel__header">
                  <FaBookmark style={{ marginRight: 6 }} />
                  {t("Salvar receita para reutilizar")}
                </div>
                <p className="create-schedule-hint">
                  {t(
                    "Salva produtos, vazão, taxa-alvo e tanque para usar em novas ordens de serviço."
                  )}
                </p>
                {savedTemplateLabel ? (
                  <p className="create-schedule-template-saved">
                    <FaCheck style={{ marginRight: 6 }} />
                    {t("Receita salva")}: <strong>{savedTemplateLabel}</strong>
                  </p>
                ) : null}
                {!showSaveTemplate ? (
                  <button
                    type="button"
                    className="btn btn-default btn-sm"
                    onClick={() => {
                      setShowSaveTemplate(true);
                      window.setTimeout(() => {
                        if (templateNameInputRef.current) {
                          templateNameInputRef.current.focus();
                        }
                      }, 50);
                    }}
                  >
                    <FaSave style={{ marginRight: 6 }} />
                    {t("Salvar receita atual")}
                  </button>
                ) : (
                  <div className="create-schedule-template-save">
                    <input
                      ref={templateNameInputRef}
                      type="text"
                      className="form-control"
                      value={templateName}
                      onChange={(event) => setTemplateName(event.target.value)}
                      placeholder={t(
                        "Nome da receita (ex.: Pulverização pré-emergente)"
                      )}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleSaveTemplate();
                        }
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => setShowSaveTemplate(false)}
                      disabled={savingTemplate}
                    >
                      {t("Cancelar")}
                    </button>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={handleSaveTemplate}
                      disabled={savingTemplate}
                    >
                      {savingTemplate ? t("Salvando...") : t("Salvar receita")}
                    </button>
                  </div>
                )}
              </div>
              ) : null}

              {showsFertilizationFields(serviceType) ? (
                <ServiceOrderFertilizationPanel
                  fertilizerName={fertilizerName}
                  onFertilizerNameChange={setFertilizerName}
                  fertilizerKgHa={fertilizerKgHa}
                  onFertilizerKgHaChange={setFertilizerKgHa}
                  t={t}
                />
              ) : null}

              {showsPlantingFields(serviceType) ? (
                <ServiceOrderPlantingPanel
                  cultivar={plantingCultivar}
                  onCultivarChange={setPlantingCultivar}
                  seedsThousandPerHa={plantingSeedsThousandPerHa}
                  onSeedsThousandPerHaChange={setPlantingSeedsThousandPerHa}
                  rowSpacingCm={plantingRowSpacingCm}
                  onRowSpacingCmChange={setPlantingRowSpacingCm}
                  pmsG={plantingPmsG}
                  onPmsGChange={setPlantingPmsG}
                  seedsPerPack={plantingSeedsPerPack}
                  onSeedsPerPackChange={setPlantingSeedsPerPack}
                  operatorSupplyUnit={plantingOperatorSupplyUnit}
                  onOperatorSupplyUnitChange={setPlantingOperatorSupplyUnit}
                  fertilizerKgHa={plantingFertilizerKgHa}
                  onFertilizerKgHaChange={setPlantingFertilizerKgHa}
                  inoculantWaterLHa={plantingInoculantWaterLHa}
                  onInoculantWaterLHaChange={setPlantingInoculantWaterLHa}
                  binderRecommendation={plantingBinderRecommendation}
                  onBinderRecommendationChange={setPlantingBinderRecommendation}
                  t={t}
                />
              ) : null}

              {showsHarvestLossFields(serviceType) ? (
                <ServiceOrderHarvestLossPanel
                  pmsG={harvestLossPmsG}
                  onPmsGChange={setHarvestLossPmsG}
                  toleratedLossKgHa={harvestLossToleratedKgHa}
                  onToleratedLossKgHaChange={setHarvestLossToleratedKgHa}
                  alertThresholdPercent={harvestLossAlertThresholdPercent}
                  onAlertThresholdPercentChange={setHarvestLossAlertThresholdPercent}
                  kgPerBag={harvestLossKgPerBag}
                  onKgPerBagChange={setHarvestLossKgPerBag}
                  minRepetitions={harvestLossMinRepetitions}
                  onMinRepetitionsChange={setHarvestLossMinRepetitions}
                  previewSeedCount={harvestLossPreviewSeedCount}
                  onPreviewSeedCountChange={setHarvestLossPreviewSeedCount}
                  t={t}
                />
              ) : null}

              <div className="create-schedule-field">
                <label htmlFor="schedule-description">{t("Descrição")}</label>
                <textarea
                  id="schedule-description"
                  className="form-control"
                  rows={4}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder={t(
                    "Descreva o que o operador deve executar no campo"
                  )}
                  required
                />
              </div>

              {showsTrackHeadingFields(serviceType) ? (
              <ServiceOrderTrackHeadingFields
                farmId={farmId}
                serviceType={serviceType}
                selectedPresetId={trackHeadingPresetId}
                onPresetIdChange={setTrackHeadingPresetId}
                onApplyLocation={applyTrackHeadingLocation}
                selectedServiceFarmIds={selectedServiceFarmIds}
                selectedLandPlotIds={selectedLandPlotIds}
                trackHeadingAudioBlob={trackHeadingAudioBlob}
                onTrackHeadingAudioChange={setTrackHeadingAudioBlob}
                publishToFarmMap={trackHeadingOnFarmMap}
                onPublishToFarmMapChange={setTrackHeadingOnFarmMap}
                t={t}
              />
              ) : null}

              {showsAudioFields(serviceType) ? (
              <ServiceOrderAudioRecorder
                audioBlob={audioBlob}
                onAudioChange={setAudioBlob}
                t={t}
                label={
                  showsHarvestLossFields(serviceType)
                    ? t("Mensagem em áudio")
                    : undefined
                }
                hint={
                  showsHarvestLossFields(serviceType)
                    ? t(
                        "Se quiser gravar uma mensagem em áudio para o operador, clique em Gravar áudio."
                      )
                    : undefined
                }
              />
              ) : null}

              <div
                className={`create-schedule-field${
                  formErrors.operators ? " create-schedule-field--error" : ""
                }`}
                ref={operatorsSectionRef}
              >
                <label>
                  <FaUserCheck style={{ marginRight: 6 }} />
                  {t("Enviar para operadores")} *
                </label>
                <p className="create-schedule-hint">
                  {t(
                    "Selecione quem deve receber no app de campo (telemetria / tablet / celular)."
                  )}
                </p>
                {formErrors.operators ? (
                  <p className="create-schedule-field-error">{formErrors.operators}</p>
                ) : null}
                <div
                  className={`create-schedule-operators${
                    formErrors.operators ? " create-schedule-operators--error" : ""
                  }`}
                >
                  {operators.length === 0 ? (
                    <p className="create-schedule-empty-operators">
                      {t("Nenhum operador cadastrado nesta fazenda.")}
                    </p>
                  ) : (
                    operators.map((operator) => (
                      <label
                        key={operator.id}
                        className="create-schedule-operator-item"
                      >
                        <input
                          type="checkbox"
                          checked={selectedOperatorIds.includes(operator.id)}
                          onChange={() => toggleOperator(operator.id)}
                        />
                        <span>{operator.name}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div className="create-schedule-field">
                <label>
                  <FaPaperPlane style={{ marginRight: 6 }} />
                  {t("Quando aparece no celular do operador")}
                </label>
                <label className="create-schedule-radio">
                  <input
                    type="radio"
                    name="send-mode"
                    checked={sendMode === "now"}
                    onChange={() => setSendMode("now")}
                  />
                  {t("Enviar agora (visível imediatamente no app)")}
                </label>
                <label className="create-schedule-radio">
                  <input
                    type="radio"
                    name="send-mode"
                    checked={sendMode === "scheduled"}
                    onChange={() => setSendMode("scheduled")}
                  />
                  {t("Agendar envio para data/hora específica")}
                </label>
                {sendMode === "scheduled" && (
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={visibleAt}
                    onChange={(event) => setVisibleAt(event.target.value)}
                    required
                  />
                )}
              </div>
            </>
          )}

          <div className="create-schedule-actions">
            <button
              type="button"
              className="btn btn-default"
              onClick={handleHide}
              disabled={submitting}
            >
              {t("Cancelar")}
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={submitting || loadingOptions}
            >
              {submitting
                ? t("Salvando...")
                : isEditing
                ? t("Salvar alterações")
                : t("Criar e enviar")}
            </button>
          </div>
        </form>
        </div>
        <ToastContainer
          position="bottom-center"
          style={{ zIndex: 16000 }}
          closeOnClick
        />
    </div>,
    document.body
  );
}

export default CreateScheduleModal;
